/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { isLoggedInSelector, usernameSelector } from "../../store/api/auth/selectors";
import { selectedGeoRegionSelector } from "../../store/api/admin/selectors";
import { basicPopoverAnchorElSelector, serverMaintenanceModeSelector } from "../../store/helpers/helperSelectors";
import { setSelectedGeoRegion } from "../../store/api/admin/reducers";
import { useStoreDispatch } from "../../store/hooks";
import { IGeoRegion } from "../../store/api/admin/types";
import { AppState } from "../../store";
import { selectProfilePermissionsSelector } from "../../store/api/auth/selectors";
import { IRouteInfo, LOCAL_STORAGE, ROUTES } from "../../globals/globals";
import ProfilePermissions from "../../utils/ProfilePermissions";
import { useRegionDisplay } from "../Administrations/AccessControls/Regions/hooks";
import { setActiveMainTab, setSelectedNavListItems, setServerMaintenanceMode } from "../../store/helpers/helperReducers";
import { setSelectedMaintenanceMode } from "../../store/filters/events/EventFilterReducer";
import { setSelectedDeviceSiteGroup, setSelectedDeviceStatus, setMaintenanceMode, resetDeviceFilters } from "../../store/filters/Nodes/reducer";
import { useLocation, useNavigate } from "react-router-dom";
import { setOpenDeviceDrawer } from "../../store/api/devices/reducers";
import { useGetSystemPropertiesQuery } from "../../store/api/admin/adminApiSlice";
import { xsdPathStr } from "../../globals/constants";
import { resetKeyword } from "../../store/search/reducer";
import { setConfigCounts } from "../../store/api/deviceConfig/reducers";

const { systemProperties } = xsdPathStr;

export const useLayout = () => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isLoggedIn = useSelector(isLoggedInSelector);
  const basicPopoverAnchorEl = useSelector(basicPopoverAnchorElSelector);
  const serverMaintenanceMode = useSelector(serverMaintenanceModeSelector);
  const { data } = useGetSystemPropertiesQuery();
  /*******************************************************************/
  /* This keeps the Nav menu, Region dropdown and SiteGroups updated
  /*******************************************************************/
  const permissions = useSelector<AppState, ProfilePermissions | null>(selectProfilePermissionsSelector);
  const selectedGeoRegion = useSelector<AppState, IGeoRegion>(selectedGeoRegionSelector);
  const [navMenuItems, setNavMenuItems] = useState<IRouteInfo[]>([ROUTES.NODES, ROUTES.TASKS]);
  const { geoRegions } = useRegionDisplay();
  const username = useSelector(usernameSelector);
  const isDashboardPath = useMemo(() => location.pathname === "/", [location.pathname]);
  const isPerformancePath = useMemo(() => location.pathname === "/externalPerformance", [location.pathname]);
  const isAdminPath = useMemo(() => location.pathname.includes("/administration"), [location.pathname]);
  const searchTooltip = "Search by 'Host Name', 'Serial Number', 'IP Address', or 'Label Name', fields for quick access.";

  const setGeoRegion = useCallback(() => {
    if (!isDashboardPath && geoRegions && geoRegions.length && geoRegions[0] != null) {
      // An empty objectKey is a valid state (e.g. "All devices"), do not auto-restore a stale single region.
      if (!selectedGeoRegion.objectKey) {
        return;
      }

      // The GeoRegion selection changed, just verify permissions
      if (geoRegions?.map((region: IGeoRegion) => region.objectKey).includes(selectedGeoRegion.objectKey)) {
        // All is well, nothing to do
        return;
      } else {
        // If we are here, be suspicious:
        //   the user no longer has permission to view the selected region, change the selected region
        // dispatch(setSelectedGeoRegion(geoRegions[0]));
        const storedNavItem = localStorage.getItem(LOCAL_STORAGE.SELECTED_NAV_ITEM);
        dispatch(setSelectedNavListItems(storedNavItem || "devices"));

        const geoRegionFromLocal = localStorage.getItem(LOCAL_STORAGE.CURRENTREGION);
        if (geoRegionFromLocal) {
          const currentGeoResion = JSON.parse(geoRegionFromLocal);
          dispatch(setSelectedGeoRegion(currentGeoResion));
        } else {
          dispatch(setSelectedGeoRegion(geoRegions[0]));
        }
      }
    } else if (selectedGeoRegion.objectKey !== "") {
      dispatch(
        setSelectedGeoRegion({
          objectKey: "",
          name: "-",
          // isDefault: false,
        })
      );
    }
  }, [dispatch, geoRegions, isDashboardPath, selectedGeoRegion.objectKey]);

  useEffect(() => {
    if (!!data) {
      dispatch(setServerMaintenanceMode(data?.[systemProperties.maintenanceModeSystem] === "true"));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (permissions) {
      if (permissions.hasAdminPermissions()) {
        setNavMenuItems([ROUTES.DASHBOARD, ROUTES.NODES, ROUTES.TASKS, ROUTES.ADMINISTRATION]);
      } else if (permissions.hasOpCodeGroup("view_tasks")) {
        setNavMenuItems([ROUTES.DASHBOARD, ROUTES.NODES, ROUTES.TASKS]);
      } else {
        setNavMenuItems([ROUTES.DASHBOARD, ROUTES.NODES]);
      }
    } else {
      setNavMenuItems([]);
    }
  }, [permissions]);

  useEffect(() => {
    setGeoRegion();
  }, [setGeoRegion]);

  useEffect(() => {
    if (isDashboardPath) {
      dispatch(
        setSelectedGeoRegion({
          objectKey: "",
          name: "all",
        })
      );
    }
  }, [dispatch, isDashboardPath]);

  useEffect(() => {
    const selectedTab = localStorage.getItem(LOCAL_STORAGE.ACTIVETAB) || "";
    if (!!location.pathname && !!selectedTab && location.pathname?.includes(selectedTab)) {
      dispatch(setActiveMainTab(selectedTab));
    } else {
      if (location.pathname === "/" || location.pathname === "") {
        dispatch(setActiveMainTab(""));
      }
      if (location.pathname?.includes("nodes") || location.pathname?.includes("device")) {
        dispatch(setActiveMainTab("nodes"));
      }
      if (location.pathname?.includes("tasks")) {
        dispatch(setActiveMainTab("tasks"));
      }
      if (location.pathname?.includes("administration")) {
        dispatch(setActiveMainTab("administration"));
      }
    }
  }, [dispatch, location.pathname]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleGeoRegionClick = (geoRegionKey: String) => {
    const newGeoRegion = geoRegions?.find((x) => x.objectKey === geoRegionKey || x.name === geoRegionKey);
    if (newGeoRegion) {
      dispatch(setSelectedGeoRegion(newGeoRegion));
      dispatch(setSelectedNavListItems(`region:${newGeoRegion.objectKey}`));
      dispatch(setSelectedDeviceSiteGroup(""));
      dispatch(setSelectedDeviceStatus(""));
      dispatch(setMaintenanceMode(""));
      dispatch(setSelectedMaintenanceMode(""));
      localStorage.setItem(LOCAL_STORAGE.CURRENTREGION, JSON.stringify(newGeoRegion));
    }
    // Navigate to nodes unless already there
    if (!location.pathname.includes("nodes")) {
      navigate("/nodes");
      dispatch(setActiveMainTab("nodes"));
    }
    dispatch(setOpenDeviceDrawer(false));
  };
  const handleOnMenuClick = (value: string) => {
    dispatch(setActiveMainTab(value));
    localStorage.setItem(LOCAL_STORAGE.ACTIVETAB, value);
    dispatch(resetKeyword());
    dispatch(resetDeviceFilters());
    dispatch(setSelectedDeviceSiteGroup(""));
    dispatch(setConfigCounts({}));
    if (value === "nodes") {
      const navItem = typeof location.state === "string" ? location.state : "devices";
      dispatch(setSelectedNavListItems(navItem));
    }
  };

  return {
    navMenuItems,
    open,
    isLoggedIn,
    handleDrawerOpen,
    handleDrawerClose,
    basicPopoverAnchorEl,
    geoRegions,
    selectedGeoRegion,
    handleGeoRegionClick,
    username,
    serverMaintenanceMode,
    handleOnMenuClick,
    isDashboardPath,
    isPerformancePath,
    isAdminPath,
    searchTooltip
  };
};
